import { BRAND_SECONDARY } from "../../../utils/Styles";
import React from "react";
import {
  Stack,
  PrimaryButton,
  Callout,
  DirectionalHint,
  Fabric,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IDetailsListProps,
  IDetailsRowStyles,
  DetailsRow,
  IColumn,
  DefaultButton,
  FocusTrapZone,
  Calendar,
  DayOfWeek,
} from "office-ui-fabric-react";
import {
  ISLADeviation,
} from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { childrenGap, DayPickerStrings } from "../../common/Constants";
import Icon from "@mdi/react";
import {
  mdiDotsVertical,
} from "@mdi/js";
import { Loading } from "../../../services/Loading";
import NoDataTile from "../../common/NoDataTile";
import {
  parseJwt,
} from "../../../services/UtilService";
import { deleteSLADeviation, getSLADeviation } from "../../../services/sla-matrix/SlaMatrixService";
import ManageSlaDeviationDialog from "./ManageSlaDeviationDialog";
import moment from "moment";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";

let calendarButtonElement: HTMLElement;

export interface SlaDeviationProps {
  customerId: string;
}

const SlaDeviation: React.FC<SlaDeviationProps> = ({ customerId }) => {
  //custom hooks
  const { showToast } = React.useContext(ToastContext);
  const tokenData = parseJwt();
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  //state
  const [state, setState] = React.useState({
    isLoading: false,
    searchObj: {
      customerId: customerId,
      date: moment().subtract(1, "month").toDate()
    },
    slaDeviation: [],
    manageSlaDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    manageActionsCallout: {
      isOpen: false,
      index: undefined,
      element: undefined,
      data: undefined,
    },
    date: moment().subtract(1, "month").toDate(),
    showCallender: false,
  } as {
    isLoading: boolean;
    searchObj: {
      customerId: string,
      date: Date | string
    };
    slaDeviation: ISLADeviation[];
    manageSlaDialog: {
      isOpen: boolean;
      index?: number;
      data?: ISLADeviation;
    };
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      index?: number;
      data?: ISLADeviation;
    };
    date: Date;
    showCallender: boolean;
  });

  React.useEffect(() => {
    const asyncFunc = async () => {
      await getSLADeviationList();
    };
    asyncFunc();
  }, []);


  const getSLADeviationList = async () => {
    let localState = { ...state };

    setState({ ...localState, isLoading: true });
    localState.searchObj.date = moment(localState.searchObj.date).format("YYYY-MM-DD");
    let result = await getSLADeviation(localState.searchObj);
    if (result?.success) {
      setState({ ...localState, slaDeviation: result.data, isLoading: false })
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result.message || "Error while getting sla deviations",
        "error"
      );
    }
  };

  const handleDialogOpen = (isOpen: boolean, index?: number) => {
    let slaRemarks = undefined;
    if (index !== undefined) {
      slaRemarks = { ...state.slaDeviation[index] };
    }
    setState({
      ...state,
      manageSlaDialog: {
        isOpen: isOpen,
        index: index,
        data: slaRemarks,
      },
    });
  };

  const handleDialogClose = (data?: ISLADeviation) => {
    let slaRemarks = [...state.slaDeviation];
    if (data) {
      if (state.manageSlaDialog.index !== undefined) {
        slaRemarks[state.manageSlaDialog.index] = data;
        showToast("SLA Deviation updated successfully", "success");
      } else {
        slaRemarks.unshift(data);
        showToast("SLA Deviation added successfully", "success");
      }
    }
    setState({
      ...state,
      manageSlaDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
      slaDeviation: slaRemarks,
    });
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    index?: number,
    data?: ISLADeviation
  ) => {
    setState({
      ...state,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        index,
        data,
      },
    });
  };

  const deleteASLADeviation = async (item: ISLADeviation, index: number) => {
    showConfirmDialog(
      "Are You Sure",
      "Do you want to delete SLA Deviation",
      async () => {
        setState({
          ...state,
          isLoading: true,
          manageActionsCallout: {
            isOpen: false,
            index: undefined,
            element: undefined,
            data: undefined,
          },
        });
        let result = await deleteSLADeviation(item);
        if (result?.success) {
          showToast("SLA Deviation deleted successfully", "success");
          let slaDeviation = [...state.slaDeviation];
          slaDeviation.splice(index, 1);
          setState({
            ...state,
            slaDeviation: slaDeviation,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              index: undefined,
              element: undefined,
              data: undefined,
            },
          });
        } else {
          setState({
            ...state,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              index: undefined,
              element: undefined,
              data: undefined,
            },
          });
          showToast(
            result.message || "Error while deleting SLA Deviation",
            "error"
          );
        }
      }
    );
  };

  const handleLoading = () => {
    setState({
      ...state,
      isLoading: !state.isLoading,
    });
  };

  const _renderItemColumn = (item: any, index: number, column: IColumn) => {
    switch (column.key) {
      case "actions":
        return (
          <span
            onClick={(ele) => handleCallOutOpenClose(true, ele, index, item)}
          >
            <Icon
              path={mdiDotsVertical}
              size={0.9}
              className="pointer"
              color={BRAND_SECONDARY}
            />
          </span>
        );
      default:
        return <span>{column.fieldName ? item[column.fieldName] : ""}</span>;
    }
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  const closeCallender = (ev?: any) => {
    setState({
      ...state,
      showCallender: false,
    });
  };

  const onSearch = async () => {
    getSLADeviationList();
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <Stack tokens={childrenGap}>
        <Stack.Item>
          <Stack tokens={childrenGap} horizontal horizontalAlign="start">
            <Stack.Item>
              <div
                ref={(calendarBtn) =>
                  (calendarButtonElement = calendarBtn!)
                }
              >
                <DefaultButton
                  text={
                    !state.date
                      ? "Select a month"
                      : moment(state.date).format("MMM-YYYY")
                  }
                  onClick={() =>
                    setState({ ...state, showCallender: true })
                  }
                  className="month-picker-btn"
                />
              </div>
              {state.showCallender && (
                <Callout
                  isBeakVisible={false}
                  // className="ms-DatePicker-callout"
                  gapSpace={0}
                  doNotLayer={false}
                  target={calendarButtonElement}
                  directionalHint={DirectionalHint.bottomLeftEdge}
                  onDismiss={() => closeCallender()}
                  setInitialFocus
                >
                  <FocusTrapZone
                    firstFocusableSelector="ms-DatePicker-day--today"
                    isClickableOutsideFocusTrap
                  >
                    <Calendar
                      // eslint-disable-next-line react/jsx-no-bind
                      onSelectDate={(date: Date | null | undefined) =>
                        setState({
                          ...state,
                          date: date as any,
                          searchObj: {
                            ...state.searchObj,
                            date: date as any
                          },
                          showCallender: false,
                        })
                      }
                      onDismiss={() => closeCallender()}
                      isMonthPickerVisible={true}
                      value={state.date}
                      firstDayOfWeek={DayOfWeek.Sunday}
                      strings={DayPickerStrings}
                      isDayPickerVisible={false}
                      highlightCurrentMonth={false}
                      highlightSelectedMonth={true}
                      showMonthPickerAsOverlay={false}
                      showGoToToday={false}
                      maxDate={moment().subtract(1, "month").toDate()}
                    />
                  </FocusTrapZone>
                </Callout>
              )}
            </Stack.Item>
            <Stack.Item>
              <PrimaryButton text="Search" onClick={() => onSearch()} />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack
            horizontal
            horizontalAlign="end"
            wrap
            tokens={childrenGap}
          >
            {tokenData.isAdmin && [0, 2, 5].includes(tokenData.role) && (
              <Stack.Item>
                <PrimaryButton
                  text="Add SLA Deviation"
                  onClick={() => handleDialogOpen(true)}
                  className="borderless-btn bg-white"
                  disabled={state.slaDeviation.length > 0}
                />
              </Stack.Item>
            )}
          </Stack>
        </Stack.Item>
        <Stack.Item>
          {state.slaDeviation && state.slaDeviation.length > 0 ? (
            <Fabric>
              <DetailsList
                items={state.slaDeviation}
                columns={
                  tokenData.isAdmin && [0, 2, 5].includes(tokenData.role)
                    ? [
                      {
                        key: "deviation",
                        name: "Deviation",
                        fieldName: "deviation",
                        minWidth: 250,
                        maxWidth: 450,
                        isResizable: true,
                      },
                      {
                        key: "actions",
                        name: "Actions",
                        fieldName: "actions",
                        minWidth: 80,
                        maxWidth: 130,
                        isResizable: true,
                      },
                    ]
                    : [
                      {
                        key: "deviation",
                        name: "Deviation",
                        fieldName: "deviation",
                        minWidth: 300,
                        maxWidth: 500,
                        isResizable: true,
                      },
                    ]
                }
                compact={true}
                setKey="set"
                onRenderItemColumn={_renderItemColumn as any}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                onRenderRow={_onRenderRow}
              />
            </Fabric>
          ) : (
            <NoDataTile title="No SLA Deviation Found for this Customer"></NoDataTile>
          )}
        </Stack.Item>
        {state.manageActionsCallout.isOpen && (
          <Callout
            styles={{ root: { width: 95, padding: 20 } }}
            role="alertdialog"
            ariaLabelledBy={
              state.manageActionsCallout && state.manageActionsCallout.data?._id
            }
            gapSpace={0}
            target={state.manageActionsCallout.element}
            directionalHint={DirectionalHint.leftCenter}
            onDismiss={() => handleCallOutOpenClose(false)}
          // setInitialFocus={true}
          >
            <Stack tokens={childrenGap}>
              <Stack.Item className="pointer">
                <span
                  onClick={() => {
                    handleDialogOpen(true, state.manageActionsCallout.index);
                  }}
                  className="callout-options"
                >
                  Edit
                </span>
              </Stack.Item>
              <Stack.Item className="pointer">
                <span
                  onClick={() => {
                    deleteASLADeviation(
                      state.manageActionsCallout.data as ISLADeviation,
                      state.manageActionsCallout.index as number
                    );
                  }}
                  className="callout-options"
                >
                  Delete
                </span>
              </Stack.Item>
            </Stack>
          </Callout>
        )}
      </Stack>
      {state.manageSlaDialog.isOpen && (
        <ManageSlaDeviationDialog
          data={state.manageSlaDialog.data}
          handleLoading={handleLoading}
          customerId={customerId}
          handleDialogClose={(data?: ISLADeviation) => handleDialogClose(data)}
          date={state.searchObj.date as Date}
        />
      )}
    </React.Fragment>
  );
};

export default SlaDeviation;
